import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Form,
  Icons,
  List,
  Select,
  Space,
  Table,
  TextField,
  useModalForm,
} from '@pankod/refine-antd'
import {
  IResourceComponentsProps,
  GetListResponse,
  useDelete,
  LogicalFilter,
  CrudFilters,
} from '@pankod/refine-core'
import React, { useEffect } from 'react'
import Head from 'next/head'

import { PopDeleteConfirm } from '@components/PopDeleteConfirm'
import ModalFormAddCalonPesertaDidik from '@resources/pendaftaran-diklat/pengajuan-peserta-didik/block/ModalFormAddCalonPesertaDidik'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { TCommonError } from 'src/interfaces/common'
import useTableWithMeta from 'src/hooks/useTableWithMeta'
import ApprovalStatus from '@resources/pendaftaran-diklat/block/ApprovalStatus'
import {
  type CalonPesertaDidikResponse,
  type CalonPesertaDidikRequest,
  APPROVAL_STATUS,
} from '@resources/pendaftaran-diklat/types'
import Filter, { TFilterForm } from '@components/ContentFilter'
import SelectFilterAsync from '@resources/pendaftaran-diklat/pengajuan-peserta-didik/block/SelectAsync'

export const PengajuanPesertaDidikList: React.FC<
  IResourceComponentsProps<GetListResponse<CalonPesertaDidikResponse>>
> = ({ options }) => {
  const {
    tableProps,
    searchFormProps,
    filters,
    tableQueryResult: { isFetching, refetch },
  } = useTableWithMeta<
    CalonPesertaDidikResponse,
    TCommonError,
    TFilterForm<CalonPesertaDidikRequest>
  >({
    resource: `diklat/peserta-diklat`,
    dataProviderName: 'lms',
    syncWithLocation: true,
    queryOptions: {
      enabled: false,
    },
    onSearch: (params) => {
      const filters: CrudFilters = []
      const { programId, programLocalId, approvalStatus, email, npsn } = params
      const shouldAddTimestamp = [
        programId,
        programLocalId,
        approvalStatus,
        npsn,
        email,
      ].some(Boolean)
      filters.push(
        { field: 'page', operator: 'eq', value: 1 },
        {
          field: 'approvalStatus',
          operator: 'eq',
          value: approvalStatus,
        },
        {
          field: 'email',
          operator: 'eq',
          value: email,
        },
        {
          field: 'npsn',
          operator: 'eq',
          value: npsn,
        },
        {
          field: 'programId',
          operator: 'eq',
          value: programId,
        },
        {
          field: 'programLocalId',
          operator: 'eq',
          value: programId ? programLocalId : undefined,
        },
        {
          field: 't',
          operator: 'eq',
          value: shouldAddTimestamp ? new Date().getTime() : undefined,
        },
      )
      return filters
    },
  })

  /** Only fetch the table when the filter list is changed */
  useEffect(() => {
    filters?.length && refetch()
  }, [filters, refetch])

  const { modalProps, formProps, show, close } = useModalForm<
    CalonPesertaDidikResponse,
    TCommonError,
    CalonPesertaDidikRequest
  >({
    action: 'create',
    resource: `diklat/peserta-diklat`,
    dataProviderName: 'lms',
    autoResetForm: true,
    redirect: false,
    successNotification: {
      message: 'Calon peserta didik berhasil diajukan',
      type: 'success',
      description: 'Sukses',
    },
    errorNotification: (error) =>
      showErrorNotification(
        error,
        'Terdapat gangguan saat mengajukan calon peserta didik',
      ),
  })
  const { mutateAsync: doDelete } = useDelete()

  const handleDeleteParticipant = async (record: CalonPesertaDidikResponse) => {
    return doDelete(
      {
        dataProviderName: 'lms',
        resource: `diklat/peserta-diklat`,
        id: record.id,
        successNotification: {
          message: 'Peserta berhasil dihapus',
          type: 'success',
          description: 'Sukses',
        },
        errorNotification: (err) =>
          showErrorNotification(
            err,
            'Terdapat gangguan saat menghapus peserta',
          ),
      },
      {
        onSuccess: () => {},
      },
    )
  }

  const filterProgramId = Form.useWatch('programId', searchFormProps.form)
  const filterEmail = Form.useWatch('email', searchFormProps.form)
  const filterNpsn = Form.useWatch('npsn', searchFormProps.form)

  return (
    <List
      title="Daftar Pengajuan Peserta Didik"
      createButtonProps={{
        children: 'Ajukan Peserta Didik',
        onClick: () => {
          show()
        },
        type: 'primary',
        icon: <Icons.UsergroupAddOutlined />,
      }}
    >
      <Head>
        <title>LMS Admin | {options?.label}</title>
      </Head>

      <Collapse className="!mb-4" bordered={false}>
        <Collapse.Panel key={'filter'} header={'Filter'}>
          <Filter
            formProps={searchFormProps}
            filters={filters as LogicalFilter[]}
            layout="vertical"
            size="small"
            className="!grid !grid-cols-2 gap-x-4 gap-y-0 grid-flow-row"
            items={[
              {
                label: 'Status Pengajuan',
                name: 'approvalStatus',
                component: (
                  <Select
                    allowClear
                    placeholder="Pilih Status"
                    options={[
                      {
                        label: 'Menunggu Persetujuan',
                        value: APPROVAL_STATUS.PENDING,
                      },
                      {
                        label: 'Sudah Disetujui',
                        value: APPROVAL_STATUS.ADMIN_APPROVED,
                      },
                      {
                        label: 'Pengajuan Ditolak',
                        value: APPROVAL_STATUS.ADMIN_REJECTED,
                      },
                    ]}
                  />
                ),
              },
              {
                label: 'Program',
                name: 'programId',
                required: !filterEmail && !filterNpsn,
                dependencies: ['email', 'npsn'],
                rules: [
                  {
                    required: !filterEmail && !filterNpsn,
                    message: 'Program harus dipilih',
                  },
                ],
                component: (
                  <SelectFilterAsync
                    resource={'diklat/programs'}
                    placeholder="Pilih Program"
                    allowClear
                    showSearch
                    onChange={() => {
                      searchFormProps?.form?.setFieldsValue({
                        programLocalId: undefined,
                      })
                    }}
                  />
                ),
              },
              {
                label: 'NPSN',
                name: 'npsn',
                placeholder: 'Cari berdasarkan NPSN peserta',
              },
              {
                label: 'Program Lokal',
                name: 'programLocalId',
                component: (
                  <SelectFilterAsync
                    resource={`diklat/programs/${filterProgramId}/local-programs`}
                    enabledQuery={!!filterProgramId}
                    placeholder="Pilih Program Lokal"
                    disabled={!filterProgramId}
                    allowClear
                    showSearch
                  />
                ),
              },
              {
                label: 'Email',
                name: 'email',
                placeholder: 'Cari berdasarkan email peserta',
                className: 'col-span-full',
              },
            ]}
          />
        </Collapse.Panel>
      </Collapse>
      {!filters?.length && (
        <Alert
          type="warning"
          showIcon
          message="Harap pilih program terlebih dahulu, atau filter berdasarkan email/NPSN"
          className="!mb-4"
        />
      )}
      <Table
        {...tableProps}
        loading={isFetching}
        expandable={{
          expandedRowRender: (record) => {
            return (
              <div className="flex flex-col gap-4">
                <Descriptions
                  title="Informasi Diklat"
                  bordered
                  size="small"
                  column={1}
                  labelStyle={{ fontWeight: 'bold' }}
                >
                  <Descriptions.Item label="Program">
                    {record.program.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Program Lokal">
                    {record.programLocal?.name ?? (
                      <TextField value="Belum ditentukan" disabled italic />
                    )}
                  </Descriptions.Item>
                </Descriptions>
                <Descriptions
                  title="Informasi Calon Peserta"
                  bordered
                  size="small"
                  column={1}
                  labelStyle={{ fontWeight: 'bold' }}
                >
                  <Descriptions.Item label="NPSN">
                    {record.npsn}
                  </Descriptions.Item>
                  <Descriptions.Item label="Nama Sekolah">
                    {record.namaSekolah}
                  </Descriptions.Item>
                  <Descriptions.Item label="Mata Pelajaran">
                    {record.mataPelajaran}
                  </Descriptions.Item>
                  <Descriptions.Item label="Provinsi">
                    {record.provinsi}
                  </Descriptions.Item>
                  <Descriptions.Item label="Kota/Kabupaten">
                    {record.kotaKabupaten}
                  </Descriptions.Item>
                  <Descriptions.Item label="Kecamatan">
                    {record.kecamatan}
                  </Descriptions.Item>
                  <Descriptions.Item label="Dokumen Pendukung">
                    {record.fileUrl ? (
                      <a
                        href="{record.fileUrl}"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icons.LinkOutlined /> Lihat Dokumen
                      </a>
                    ) : (
                      <TextField value="Kosong" type="secondary" italic />
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )
          },
        }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="id"
          title="ID"
          render={(value) => <TextField value={value} />}
          className="align-top"
        />
        <Table.Column
          dataIndex="nama"
          title="Nama Calon Peserta"
          render={(value) => <TextField value={value} />}
          className="align-top"
        />
        <Table.Column
          dataIndex="email"
          title="Email"
          render={(value) => <TextField value={value} />}
          className="align-top"
        />
        <Table.Column
          dataIndex="phone"
          title="Nomor HP"
          render={(value) =>
            !!value ? (
              <TextField value={value} />
            ) : (
              <TextField value="Kosong" italic type="secondary" />
            )
          }
          className="align-top"
        />
        <Table.Column
          dataIndex="approvalStatus"
          title="Status Pengajuan"
          render={(value: ApprovalStatus) => <ApprovalStatus status={value} />}
          className="align-top"
        />
        <Table.Column<CalonPesertaDidikResponse>
          title="Aksi"
          className="align-top"
          render={(_, record) => (
            <Space>
              <PopDeleteConfirm
                title="Apakah Anda yakin ingin menghapus calon peserta ini?"
                onConfirm={async () => {
                  await handleDeleteParticipant(record)
                }}
                okText="Hapus"
                cancelText="Kembali"
                okButtonProps={{ danger: true }}
                disabled={record.approvalStatus !== 'PENDING'}
              >
                <Button
                  disabled={record.approvalStatus !== 'PENDING'}
                  danger
                  icon={<Icons.DeleteOutlined />}
                  size="small"
                >
                  Hapus
                </Button>
              </PopDeleteConfirm>
            </Space>
          )}
        />
      </Table>
      <ModalFormAddCalonPesertaDidik
        formProps={formProps}
        modalProps={modalProps}
        onClose={close}
      />
    </List>
  )
}
